import { useContext, useEffect, useRef, useMemo, lazy, Suspense } from 'react';
import { ObjectPageContext } from '../../../pages/Context/ObjectPageContext';
import useScrollListner from '../../CustomHooks/useScrollListner';
import MediaViewImage from './MediaViewImage';
import MediaViewHeader from './MediaViewHeader';
import ImageView from '../../ObjectPageComponents/ObjectHero/ImageView';
import MediaViewOption from './MediaViewOption';
const MediaView3D = lazy(() => import('./MediaView3D'));
const MediaViewHeli = lazy(() => import('./MediaViewHeli'));
const MediaPlayer = lazy(() => import('./MediaPlayer'));

const MediaContainer = () => {
  const {
    mediaOption,
    setMediaOption,
    mediaView,
    setMediaView,
    estate,
    isBigScreen,
  } = useContext(ObjectPageContext);

  useEffect(() => {
    if (mediaView) {
      const htmlElement = document.querySelector('html');
      if (htmlElement) {
        htmlElement.style.overflow = 'hidden';
      }
      return () => {
        if (htmlElement) {
          htmlElement.style.overflow = '';
        }
      };
    }
  }, []);

  //View options
  const imageView = estate?.images.length > 0;
  const movieView =
    estate?.links.filter(
      (link) =>
        link?.category && link.category.toLowerCase().indexOf('film') > -1
    ).length > 0;
  const constructPlan =
    estate?.images.filter(
      (image) =>
        image?.category && image.category.toLowerCase() === 'planlösning'
    ).length > 0;
  const insideView =
    estate?.links.filter(
      (link) => link?.category && link.category.toLowerCase() === '3d visning'
    ).length > 0;
  const heliView =
    estate?.links.filter(
      (link) => link?.category && link.category.toLowerCase() === 'helikoptervy'
    ).length > 0;

  const mediaViewContainer = useRef(null);
  const container = useMemo(() => mediaViewContainer, [mediaViewContainer]);
  const isTouched = useScrollListner(container);
  let isScrollingDown = useMemo(() => isTouched, [isTouched]);

  useEffect(() => {
    if (mediaViewContainer !== null) {
      mediaViewContainer.current.scrollTop = 0;
    }
  }, [mediaOption]);

  return (
    <>
      <MediaViewHeader
        displayMediaView={setMediaView}
        setMediaOption={setMediaOption}
      />
      <div
        ref={mediaViewContainer}
        className="top-0 lg:top-14 left-0 z-[1002] fixed bg-white pt-12 lg:pt-0 pb-[60px] lg:pb-[50px] w-screen h-screen lg:h-[calc(100vh-64px)] overflow-x-hidden overflow-y-scroll overscroll-contain"
      >
        {mediaOption === 'imageView' && (
          <div className="flex flex-col gap-2 m-auto max-w-7xl">
            <ImageView images={estate?.images} />
          </div>
        )}
        {mediaOption === 'constructPlan' && (
          <div className="flex flex-col gap-2 m-auto max-w-5xl">
            {estate?.images
              .filter((image) => image.category.toLowerCase() === 'planlösning')
              .map((image, index) => {
                return (
                  <MediaViewImage
                    url={image?.path}
                    key={image?.path}
                    text={image?.text}
                    index={index}
                    alt={
                      image?.text
                        ? image?.text
                        : 'Planlösning ' + estate?.addressInfo?.streetAddress
                    }
                  />
                );
              })}
          </div>
        )}
        {mediaOption === 'movieView' && (
          <div className="relative -z-10 bg-white pt-12 md:pt-10 lg:pt-0 h-full overflow-auto mediaPlayer-container">
            {estate?.links
              ?.filter((link) => link.category.toLowerCase() === 'film')
              .map((link, index) => {
                return (
                  <div
                    key={index}
                    className="m-auto md:max-w-7xl mediaPlayer overscroll-none"
                  >
                    <Suspense fallback={<></>}>
                      <MediaPlayer videoUrl={link.links[0]} />
                    </Suspense>
                  </div>
                );
              })}
          </div>
        )}
        {mediaOption === '3DView' && (
          <div className="flex flex-col m-auto max-w-7xl overscroll-none">
            {estate?.links
              ?.filter((link) => link.category.toLowerCase() === '3d visning')
              .map((link, index) => {
                return (
                  <Suspense fallback={<></>}>
                    <MediaView3D url={link.links[index]} key={index} />
                  </Suspense>
                );
              })}
          </div>
        )}
        {mediaOption === 'heliView' && (
          <div className="flex flex-col items-center m-auto max-w-7xl overscroll-none">
            {estate?.links
              ?.filter(
                (link) =>
                  link?.category &&
                  link.category.toLowerCase() === 'helikoptervy'
              )
              .map((link, index) => {
                return (
                  <Suspense fallback={<></>}>
                    <MediaViewHeli url={link.links[index]} key={index} />
                  </Suspense>
                );
              })}
          </div>
        )}
        <div className="top-1/2 left-1/2 fixed lg:shadow-[inset_-10px_0px_5px_-10px_#00000014] m-auto w-full max-w-7xl h-[100dvh] lg:h-[400px] -translate-x-1/2 -translate-y-1/2 overflow-x-hidden overflow-y-hidden pointer-events-none">
          <div
            className={`${
              isScrollingDown ? '-bottom-20' : 'bottom-4'
            } transition-all duration-300 ease-in-out w-[98%] h-fit flex md:w-11/12 flex-row flex-nowrap lg:flex-col gap-[1px] fixed lg:absolute left-1/2 lg:left-auto -translate-x-1/2 lg:-translate-x-0 lg:right-[-204px] lg:hover:right-0 bg-grey100 lg:bg-blue700 rounded-md overflow-x-auto lg:overflow-hidden shadow-bs60 z-50 pointer-events-auto ${
              isBigScreen && 'media-option-menu-side'
            }`}
          >
            {imageView && (
              <MediaViewOption
                label="Bilder"
                id="imageView"
                icon="/Images/Icons/objectPage/image-black.svg"
                mediaOption={mediaOption}
                setMediaOption={setMediaOption}
              />
            )}
            {movieView && (
              <MediaViewOption
                label="Filmvisning"
                id="movieView"
                icon="/Images/Icons/objectPage/movie-black.svg"
                mediaOption={mediaOption}
                setMediaOption={setMediaOption}
              />
            )}
            {constructPlan && (
              <MediaViewOption
                label="Planlösning"
                id="constructPlan"
                icon="/Images/Icons/objectPage/constructplan-black.svg"
                mediaOption={mediaOption}
                setMediaOption={setMediaOption}
              />
            )}
            {insideView && (
              <MediaViewOption
                label="3D-visning"
                id="3DView"
                icon="/Images/Icons/objectPage/3dView-black.svg"
                mediaOption={mediaOption}
                setMediaOption={setMediaOption}
              />
            )}
            {heliView && (
              <MediaViewOption
                label="Helikoptervy"
                id="heliView"
                icon="/Images/Icons/objectPage/360-black.svg"
                mediaOption={mediaOption}
                setMediaOption={setMediaOption}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
export default MediaContainer;
